import React, { useState, useEffect } from "react";
import {
  calculateHoleClearance,
  CLEARANCE_TABLE,
  METRIC_CLEARANCE_TABLE,
  HOLE_CALCULATION_MODES,
  HOLE_MAKING_PROCESSES,
  HOLE_MAKING_CONDITIONS,
} from "../../utils/calculations";
import FitAnalysisPanel from "./FitAnalysisPanel";

const FIT_LABELS = {
  close: "Slip Fit",
  normal: "Clearance Fit",
  loose: "Free Fit",
};

const FIT_TOLERANCE_NOTES = {
  close: "Controlled positive clearance for accurate bolt location.",
  normal: "General-purpose positive clearance for most bolted assemblies.",
  loose: "Larger positive clearance for coatings, weldments, flame-cut parts, or field fit-up.",
};

const INCH_FASTENER_MAJOR_DIAMETERS = {
  "#0": 0.06,
  "#1": 0.073,
  "#2": 0.086,
  "#3": 0.099,
  "#4": 0.112,
  "#5": 0.125,
  "#6": 0.138,
  "#8": 0.164,
  "#10": 0.19,
  "#12": 0.216,
  "1/4": 0.25,
  "5/16": 0.3125,
  "3/8": 0.375,
  "7/16": 0.4375,
  "1/2": 0.5,
  "9/16": 0.5625,
  "5/8": 0.625,
  "3/4": 0.75,
  "7/8": 0.875,
  "1": 1,
  "1-1/8": 1.125,
  "1-1/4": 1.25,
  "1-3/8": 1.375,
  "1-1/2": 1.5,
};

const STANDARD_LABELS = {
  asme_inch: "ASME B18.2.8 inch clearance",
  iso_metric: "ISO 273 metric clearance",
};

const MODE_LABELS = {
  [HOLE_CALCULATION_MODES.standard]: "Published Fit / Clearance Table",
  [HOLE_CALCULATION_MODES.custom]: "Custom Decimal Hole",
};

const PROCESS_LABELS = {
  [HOLE_MAKING_PROCESSES.drill]: "Drill Only",
  [HOLE_MAKING_PROCESSES.drill_ream]: "Drill + Ream",
  [HOLE_MAKING_PROCESSES.drill_bore]: "Drill + Bore",
  [HOLE_MAKING_PROCESSES.bore_only]: "Bore Only",
};

const CONDITION_LABELS = {
  [HOLE_MAKING_CONDITIONS.sharp_rigid]: "Sharp / Rigid Setup",
  [HOLE_MAKING_CONDITIONS.normal]: "Normal",
  [HOLE_MAKING_CONDITIONS.worn]: "Worn",
  [HOLE_MAKING_CONDITIONS.high_runout]: "High Runout / Less Rigid",
};

const formatValue = (value, decimals = 4) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toFixed(decimals);
};

const HoleSizingCalculator = ({ onSave }) => {
  const [params, setParams] = useState({
    standard: "asme_inch",
    mode: HOLE_CALCULATION_MODES.standard,
    boltSize: "1/4",
    fit: "normal",
    customHoleSize: 0.257,
    process: HOLE_MAKING_PROCESSES.drill,
    condition: HOLE_MAKING_CONDITIONS.normal,
  });

  const [results, setResults] = useState(null);

  useEffect(() => {
    const calcResults = calculateHoleClearance(params);
    setResults(calcResults);
  }, [params]);

  const handleInputChange = (field, value) => {
    setParams((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "standard"
        ? {
            boltSize: value === "iso_metric" ? "M6" : "1/4",
            customHoleSize: value === "iso_metric" ? 6.6 : 0.257,
          }
        : {}),
    }));
  };

  const handleNumberChange = (field, value) => {
    setParams((prev) => ({
      ...prev,
      [field]: value === "" ? "" : parseFloat(value),
    }));
  };

  const resetForm = () => {
    setParams({
      standard: "asme_inch",
      mode: HOLE_CALCULATION_MODES.standard,
      boltSize: "1/4",
      fit: "normal",
      customHoleSize: 0.257,
      process: HOLE_MAKING_PROCESSES.drill,
      condition: HOLE_MAKING_CONDITIONS.normal,
    });
  };

  const handleSave = () => {
    if (!results || results.errors || !onSave) return;

    const summary =
      params.mode === HOLE_CALCULATION_MODES.standard
        ? `${params.boltSize}" ${FIT_LABELS[params.fit]} | ${formatValue(
            results.targetHoleSize
          )}" target hole`
        : `${formatValue(params.customHoleSize)}" custom hole | ${
            PROCESS_LABELS[params.process]
          }`;

    onSave({
      summary,
      input: {
        mode: MODE_LABELS[params.mode],
        standard: STANDARD_LABELS[params.standard],
        boltSize:
          params.mode === HOLE_CALCULATION_MODES.standard
            ? `${params.boltSize}"`
            : null,
        fit:
          params.mode === HOLE_CALCULATION_MODES.standard
            ? FIT_LABELS[params.fit]
            : null,
        customHoleSize:
          params.mode === HOLE_CALCULATION_MODES.custom
            ? `${formatValue(params.customHoleSize)}"`
            : null,
        process: PROCESS_LABELS[params.process],
        condition: CONDITION_LABELS[params.condition],
      },
      results: {
        targetHoleSize:
          results.targetHoleSize != null
            ? `${formatValue(results.targetHoleSize)}"`
            : null,
        recommendedDrill: results.recommendedDrillSizeName || null,
        recommendedDrillDecimal:
          results.recommendedDrillSizeDecimal != null
            ? `${formatValue(results.recommendedDrillSizeDecimal)}"`
            : null,
        expectedHoleMin:
          results.expectedHoleMin != null
            ? `${formatValue(results.expectedHoleMin)}"`
            : null,
        expectedHoleMax:
          results.expectedHoleMax != null
            ? `${formatValue(results.expectedHoleMax)}"`
            : null,
        prepHoleMin:
          results.prepHoleMin != null
            ? `${formatValue(results.prepHoleMin)}"`
            : null,
        prepHoleMax:
          results.prepHoleMax != null
            ? `${formatValue(results.prepHoleMax)}"`
            : null,
        allowanceMin:
          results.allowanceMin != null
            ? `${formatValue(results.allowanceMin)}"`
            : null,
        allowanceMax:
          results.allowanceMax != null
            ? `${formatValue(results.allowanceMax)}"`
            : null,
      },
      warnings: results.warnings || [],
      confidence: results.confidence || "N/A",
      processNotes: results.processNotes || [],
    });
  };

  const copyToClipboard = () => {
    if (!results || results.errors) return;

    const lines = [
      "ManuCalc - Hole Clearance Calculator",
      `Standard: ${STANDARD_LABELS[params.standard]}`,
      `Mode: ${MODE_LABELS[params.mode]}`,
      params.mode === HOLE_CALCULATION_MODES.standard
        ? `Fastener Size: ${params.boltSize}`
        : `Custom Hole Size: ${params.customHoleSize} ${isMetric ? "mm" : "in"}`,
      params.mode === HOLE_CALCULATION_MODES.standard
        ? `Fit Type: ${FIT_LABELS[params.fit]}`
        : null,
      `Process: ${PROCESS_LABELS[params.process]}`,
      `Condition: ${CONDITION_LABELS[params.condition]}`,
      `Target Hole Size: ${primaryHole(results.targetHoleSize, results.targetHoleMetric)} (${secondaryHole(results.targetHoleSize, results.targetHoleMetric)})`,
      results.recommendedDrillSizeName
        ? `Recommended Drill: ${results.recommendedDrillSizeName} (${primaryHole(results.recommendedDrillSizeDecimal, results.recommendedDrillMetric)})`
        : null,
      results.expectedHoleMin != null && results.expectedHoleMax != null
        ? `Expected Hole Range: ${primaryHole(results.expectedHoleMin, results.expectedHoleMinMetric)} - ${primaryHole(results.expectedHoleMax, results.expectedHoleMaxMetric)}`
        : null,
      results.prepHoleMin != null && results.prepHoleMax != null
        ? `Prep Hole Range: ${primaryHole(results.prepHoleMin, results.prepHoleMinMetric)} - ${primaryHole(results.prepHoleMax, results.prepHoleMaxMetric)}`
        : null,
      results.allowanceMin != null && results.allowanceMax != null
        ? `Allowance Range: ${primaryHole(results.allowanceMin, results.allowanceMinMetric)} - ${primaryHole(results.allowanceMax, results.allowanceMaxMetric)}`
        : null,
    ].filter(Boolean);

    navigator.clipboard.writeText(lines.join("\n"));
  };

  const hasErrors = results?.errors && Object.keys(results.errors).length > 0;
  const isMetric = params.standard === "iso_metric";
  const clearanceTable = isMetric ? METRIC_CLEARANCE_TABLE : CLEARANCE_TABLE;
  const primaryHole = (inchValue, mmValue) =>
    isMetric ? `${formatValue(mmValue, 3)} mm` : `${formatValue(inchValue)}"`;
  const secondaryHole = (inchValue, mmValue) =>
    isMetric ? `${formatValue(inchValue)} in` : `${formatValue(mmValue, 3)} mm`;
  const nominalFastenerSize = isMetric
    ? parseFloat(String(params.boltSize).replace("M", ""))
    : INCH_FASTENER_MAJOR_DIAMETERS[params.boltSize];
  const getFitClearanceLabel = (fit) => {
    const targetSize = params.mode === HOLE_CALCULATION_MODES.standard
      ? clearanceTable[params.boltSize]?.[fit]
      : null;
    const clearance = Number.isFinite(Number(nominalFastenerSize)) && Number.isFinite(Number(targetSize))
      ? Number(targetSize) - Number(nominalFastenerSize)
      : null;

    if (clearance == null) return "N/A";
    return isMetric
      ? `+${formatValue(clearance, 3)} mm / +${formatValue(clearance / 25.4)} in`
      : `+${formatValue(clearance)} in / +${formatValue(clearance * 25.4, 3)} mm`;
  };
  const fitClearanceLabel = getFitClearanceLabel(params.fit);

  return (
    <div
      className="calculator-container"
      style={{
        background: "var(--bg-surface)",
        color: "var(--text-secondary)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        padding: "24px",
      }}
    >
      <div className="calculator-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
          ⭕ Hole Clearance Calculator
        </h1>
        <p style={{ color: "var(--text-muted)", margin: 0 }}>
          Size fastener clearance holes using industry fit terminology,
          published clearance-hole tables, process allowance, setup condition,
          and expected hole-size behavior.
        </p>
      </div>

      <FitAnalysisPanel onSave={onSave} />

      <div
        className="calculator-content"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "24px",
        }}
      >
        <div
          className="calculator-form"
          style={{
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "20px",
          }}
        >
          <div className="form-section" style={{ marginBottom: "24px" }}>
            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                htmlFor="standard"
                style={{
                  display: "block",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                  marginBottom: "8px",
                }}
              >
                Clearance Standard
              </label>
              <select
                id="standard"
                value={params.standard}
                onChange={(e) => handleInputChange("standard", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              >
                <option value="asme_inch">ASME B18.2.8 - Inch clearance-hole series</option>
                <option value="iso_metric">ISO 273 - Metric clearance-hole series</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                htmlFor="mode"
                style={{
                  display: "block",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                  marginBottom: "8px",
                }}
              >
                Input Mode
              </label>
              <select
                id="mode"
                value={params.mode}
                onChange={(e) => handleInputChange("mode", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              >
                <option value={HOLE_CALCULATION_MODES.standard}>
                  Published Fit / Clearance Table
                </option>
                <option value={HOLE_CALCULATION_MODES.custom}>
                  Custom Decimal Hole
                </option>
              </select>
            </div>

            {params.mode === HOLE_CALCULATION_MODES.standard ? (
              <>
                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label
                    htmlFor="boltSize"
                    style={{
                      display: "block",
                      color: "var(--text-primary)",
                      fontWeight: 600,
                      marginBottom: "8px",
                    }}
                  >
                    Fastener Size
                  </label>
                  <select
                    id="boltSize"
                    value={params.boltSize}
                    onChange={(e) => handleInputChange("boltSize", e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1px solid var(--border-color)",
                      background: "var(--bg-surface)",
                      color: "var(--text-primary)",
                      boxSizing: "border-box",
                    }}
                  >
                    {Object.keys(clearanceTable).map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label
                    htmlFor="fit"
                    style={{
                      display: "block",
                      color: "var(--text-primary)",
                      fontWeight: 600,
                      marginBottom: "8px",
                    }}
                  >
                    Fit / Clearance Class
                  </label>
                  <select
                    id="fit"
                    value={params.fit}
                    onChange={(e) => handleInputChange("fit", e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1px solid var(--border-color)",
                      background: "var(--bg-surface)",
                      color: "var(--text-primary)",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="close">Slip Fit | {getFitClearanceLabel("close")}</option>
                    <option value="normal">Clearance Fit | {getFitClearanceLabel("normal")}</option>
                    <option value="loose">Free Fit | {getFitClearanceLabel("loose")}</option>
                  </select>
                  <small style={{ color: "var(--text-muted)", display: "block", marginTop: "8px", lineHeight: 1.5 }}>
                    {FIT_LABELS[params.fit]}: {fitClearanceLabel} over nominal fastener diameter. Target hole: {primaryHole(results?.targetHoleSize, results?.targetHoleMetric)}.
                    {" "}{FIT_TOLERANCE_NOTES[params.fit]} Press Fit: interference fit, typically negative clearance such as -0.0005 to -0.0030 in on small shop fits; use the drawing's shaft/hole limits such as ISO H7/p6, FN/force fit, or the bearing/bushing maker's data.
                  </small>
                </div>
              </>
            ) : (
              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="customHoleSize"
                  style={{
                    display: "block",
                    color: "var(--text-primary)",
                    fontWeight: 600,
                    marginBottom: "8px",
                  }}
                >
                  Custom Hole Size ({isMetric ? "mm" : "decimal inches"})
                </label>
                <input
                  id="customHoleSize"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={params.customHoleSize}
                  onChange={(e) =>
                    handleNumberChange("customHoleSize", e.target.value)
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    background: "var(--bg-surface)",
                    color: "var(--text-primary)",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            )}

            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                htmlFor="process"
                style={{
                  display: "block",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                  marginBottom: "8px",
                }}
              >
                Finishing Process
              </label>
              <select
                id="process"
                value={params.process}
                onChange={(e) => handleInputChange("process", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              >
                <option value={HOLE_MAKING_PROCESSES.drill}>Drill Only</option>
                <option value={HOLE_MAKING_PROCESSES.drill_ream}>
                  Drill + Ream
                </option>
                <option value={HOLE_MAKING_PROCESSES.drill_bore}>
                  Drill + Bore
                </option>
                <option value={HOLE_MAKING_PROCESSES.bore_only}>
                  Bore Only
                </option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: "20px" }}>
              <label
                htmlFor="condition"
                style={{
                  display: "block",
                  color: "var(--text-primary)",
                  fontWeight: 600,
                  marginBottom: "8px",
                }}
              >
                Tool / Setup Condition
              </label>
              <select
                id="condition"
                value={params.condition}
                onChange={(e) => handleInputChange("condition", e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                  boxSizing: "border-box",
                }}
              >
                <option value={HOLE_MAKING_CONDITIONS.sharp_rigid}>
                  Sharp / Rigid Setup
                </option>
                <option value={HOLE_MAKING_CONDITIONS.normal}>Normal</option>
                <option value={HOLE_MAKING_CONDITIONS.worn}>Worn</option>
                <option value={HOLE_MAKING_CONDITIONS.high_runout}>
                  High Runout / Less Rigid
                </option>
              </select>
            </div>
          </div>

          <div
            className="form-actions"
            style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
          >
            <button onClick={resetForm} className="btn-secondary" type="button">
              Reset
            </button>
          </div>
        </div>

        <div
          className="calculator-results"
          style={{
            background: "var(--bg-surface-alt)",
            border: "1px solid var(--border-color)",
            borderRadius: "12px",
            padding: "20px",
            minHeight: "100%",
          }}
        >
          {results && !hasErrors ? (
            <>
              <div
                className="results-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "20px",
                }}
              >
                <h3 style={{ color: "var(--text-primary)", margin: 0 }}>
                  Results
                </h3>
                <div
                  className="confidence-indicator"
                  style={{ color: "var(--text-secondary)" }}
                >
                  Confidence:{" "}
                  <span
                    className={`confidence-${String(
                      results.confidence || "high"
                    ).toLowerCase()}`}
                    style={{ color: "var(--accent)", fontWeight: 700 }}
                  >
                    {results.confidence}
                  </span>
                </div>
              </div>

              <div
                className="results-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "12px",
                  marginBottom: "20px",
                }}
              >
                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Target Hole Size
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {primaryHole(results.targetHoleSize, results.targetHoleMetric)}
                    <small style={{ display: "block", color: "var(--text-muted)", fontSize: "12px", marginTop: "4px" }}>
                      {secondaryHole(results.targetHoleSize, results.targetHoleMetric)}
                    </small>
                  </span>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Recommended Drill
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.recommendedDrillSizeName || "—"}
                  </span>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Drill Decimal
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "18px",
                    }}
                  >
                    {results.recommendedDrillSizeDecimal != null
                      ? primaryHole(results.recommendedDrillSizeDecimal, results.recommendedDrillMetric)
                      : "—"}
                  </span>
                </div>

                <div
                  className="result-item"
                  style={{
                    background: "var(--bg-surface)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "10px",
                    padding: "14px",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "var(--text-muted)",
                      fontSize: "14px",
                      marginBottom: "6px",
                    }}
                  >
                    Expected Hole Range
                  </label>
                  <span
                    style={{
                      color: "var(--text-primary)",
                      fontWeight: 700,
                      fontSize: "16px",
                    }}
                  >
                    {results.expectedHoleMin != null &&
                    results.expectedHoleMax != null
                      ? `${primaryHole(results.expectedHoleMin, results.expectedHoleMinMetric)} - ${primaryHole(
                          results.expectedHoleMax,
                          results.expectedHoleMaxMetric
                        )}`
                      : "—"}
                  </span>
                </div>
              </div>

              <div
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "10px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <h4
                  style={{
                    color: "var(--text-primary)",
                    marginTop: 0,
                    marginBottom: "10px",
                  }}
                >
                  Process Guidance
                </h4>
                <div style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Standard:
                    </strong>{" "}
                    {STANDARD_LABELS[params.standard]}
                  </div>

                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Mode:
                    </strong>{" "}
                    {MODE_LABELS[params.mode]}
                  </div>

                  {params.mode === HOLE_CALCULATION_MODES.standard ? (
                    <>
                      <div>
                        <strong style={{ color: "var(--text-primary)" }}>
                          Fastener Size:
                        </strong>{" "}
                        {params.boltSize}
                      </div>
                      <div>
                        <strong style={{ color: "var(--text-primary)" }}>
                          Fit:
                        </strong>{" "}
                        {FIT_LABELS[params.fit]}
                      </div>
                    </>
                  ) : (
                    <div>
                      <strong style={{ color: "var(--text-primary)" }}>
                        Custom Hole:
                      </strong>{" "}
                      {params.customHoleSize} {isMetric ? "mm" : "in"}
                    </div>
                  )}

                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Process:
                    </strong>{" "}
                    {PROCESS_LABELS[params.process]}
                  </div>
                  <div>
                    <strong style={{ color: "var(--text-primary)" }}>
                      Condition:
                    </strong>{" "}
                    {CONDITION_LABELS[params.condition]}
                  </div>

                  {results.prepHoleMin != null && results.prepHoleMax != null && (
                    <div>
                      <strong style={{ color: "var(--text-primary)" }}>
                        Prep Hole Range:
                      </strong>{" "}
                      {primaryHole(results.prepHoleMin, results.prepHoleMinMetric)} -{" "}
                      {primaryHole(results.prepHoleMax, results.prepHoleMaxMetric)}
                    </div>
                  )}

                  {results.allowanceMin != null &&
                    results.allowanceMax != null && (
                      <div>
                        <strong style={{ color: "var(--text-primary)" }}>
                          Allowance Range:
                        </strong>{" "}
                        {primaryHole(results.allowanceMin, results.allowanceMinMetric)} -{" "}
                        {primaryHole(results.allowanceMax, results.allowanceMaxMetric)}
                      </div>
                    )}
                </div>
              </div>

              {results.processNotes?.length > 0 && (
                <div
                  style={{
                    background: "rgba(59, 130, 246, 0.10)",
                    border: "1px solid rgba(59, 130, 246, 0.25)",
                    borderRadius: "10px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      color: "var(--text-primary)",
                      marginTop: 0,
                      marginBottom: "10px",
                    }}
                  >
                    ℹ️ Shop Notes
                  </h4>
                  <ul
                    style={{
                      paddingLeft: "20px",
                      color: "var(--text-secondary)",
                      marginBottom: 0,
                    }}
                  >
                    {results.processNotes.map((note, index) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}

              {results.warnings?.length > 0 && (
                <div
                  className="warnings"
                  style={{
                    background: "rgba(245, 158, 11, 0.12)",
                    border: "1px solid rgba(245, 158, 11, 0.35)",
                    borderRadius: "10px",
                    padding: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <h4
                    style={{
                      color: "var(--text-primary)",
                      marginBottom: "10px",
                    }}
                  >
                    ⚠️ Warnings
                  </h4>
                  <ul
                    style={{
                      paddingLeft: "20px",
                      color: "var(--text-secondary)",
                      marginBottom: 0,
                    }}
                  >
                    {results.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div
                className="results-actions"
                style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
              >
                <button
                  onClick={handleSave}
                  className="btn-primary"
                  type="button"
                >
                  💾 Save Calculation
                </button>
                <button
                  onClick={copyToClipboard}
                  className="btn-secondary"
                  type="button"
                >
                  📄 Copy
                </button>
              </div>
            </>
          ) : (
            <div
              style={{
                color: "var(--text-muted)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "180px",
                textAlign: "center",
              }}
            >
              Enter a standard clearance fit or custom decimal hole to get
              process-aware holemaking guidance.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HoleSizingCalculator;
